import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Switch } from './ui/switch';
import { Terminal, X, ChevronDown, ChevronUp, Minus } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';
import { TranscriptionModel } from '../services/voiceTranscriptionService';
import { useDevConsoleStore } from '../stores/useDevConsoleStore';

interface DevConsoleProps {}

export const DevConsole: React.FC<DevConsoleProps> = () => {
  const { translate } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(true);
  const {
    transcriptionModel,
    chatModel,
    simulatedRoles,
    isRoleSimulationEnabled,
    setTranscriptionModel,
    setChatModel,
    setSimulatedRoles,
    setRoleSimulationEnabled,
  } = useDevConsoleStore();

  // Check if we're in development mode
  const isDevelopment =
    import.meta.env.VITE_NODE_ENV === 'development' ||
    import.meta.env.VITE_NODE_ENV === 'dev' ||
    window.location.hostname === 'localhost';

  // Don't render in production
  if (!isDevelopment) {
    return null;
  }

  // Load saved settings from localStorage
  useEffect(() => {
    const savedTranscriptionModel = localStorage.getItem(
      'dev-console-transcription-model'
    ) as TranscriptionModel | null;
    const savedChatModel = localStorage.getItem('dev-console-chat-model');
    const savedSimulatedRoles = localStorage.getItem(
      'dev-console-simulated-roles'
    );
    const savedRoleSimulationEnabled = localStorage.getItem(
      'dev-console-role-simulation-enabled'
    );

    if (savedTranscriptionModel) {
      setTranscriptionModel(savedTranscriptionModel);
    }
    if (savedChatModel) {
      setChatModel(savedChatModel);
    }
    if (savedSimulatedRoles) {
      try {
        const roles = JSON.parse(savedSimulatedRoles);
        setSimulatedRoles(roles);
        if (savedRoleSimulationEnabled === 'true') {
          setRoleSimulationEnabled(true);
        }
      } catch (e) {
        console.error('Failed to parse saved roles:', e);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTranscriptionModelChange = (model: TranscriptionModel) => {
    setTranscriptionModel(model);
    localStorage.setItem('dev-console-transcription-model', model);
  };

  const handleChatModelChange = (model: string) => {
    setChatModel(model);
    localStorage.setItem('dev-console-chat-model', model);
  };

  const handleRoleSimulationToggle = (enabled: boolean) => {
    setRoleSimulationEnabled(enabled);
    localStorage.setItem(
      'dev-console-role-simulation-enabled',
      enabled ? 'true' : 'false'
    );
    if (enabled && simulatedRoles.length > 0) {
      // Apply roles immediately when enabling
      setSimulatedRoles(simulatedRoles);
    } else if (!enabled) {
      // Clear roles when disabling
      setSimulatedRoles([]);
      // Clear localStorage to prevent restoration on navigation
      localStorage.removeItem('dev-console-simulated-roles');
    }
  };

  const handleRoleChange = (value: string) => {
    // Single role selection - replace array with single role
    const roles = [value];
    setSimulatedRoles(roles);
    localStorage.setItem('dev-console-simulated-roles', JSON.stringify(roles));
    // Automatically enable role simulation when a role is selected
    if (!isRoleSimulationEnabled) {
      setRoleSimulationEnabled(true);
      localStorage.setItem('dev-console-role-simulation-enabled', 'true');
    }
  };

  if (isMinimized) {
    return (
      <div className="fixed bottom-20 left-4 lg:bottom-4 z-[9999]">
        <Button
          onClick={() => setIsMinimized(false)}
          variant="outline"
          size="icon"
          className="h-10 w-10 shadow-lg"
          title="Open Dev Console"
        >
          <Terminal className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-20 left-4 lg:bottom-4 z-[9999] w-96 max-w-[calc(100vw-2rem)]">
      <Card className="shadow-2xl border-2 border-yellow-500/50 bg-background">
        <CardHeader className="pb-2 pt-3 space-y-0">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Terminal className="h-4 w-4 text-yellow-500" />
              Dev Console
            </CardTitle>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => setIsMinimized(true)}
                title="Minimize"
              >
                <Minus className="h-3 w-3" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => setIsOpen(!isOpen)}
                title={isOpen ? 'Close' : 'Open'}
              >
                {isOpen ? (
                  <ChevronDown className="h-3 w-3" />
                ) : (
                  <ChevronUp className="h-3 w-3" />
                )}
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Development mode only - Not available in production
          </p>
        </CardHeader>
        {isOpen && (
          <CardContent className="space-y-4 pt-0">
            {/* Transcription Model Selection */}
            <div className="space-y-2">
              <Label className="text-xs font-medium">Transcription Model</Label>
              <Select
                value={transcriptionModel}
                onValueChange={handleTranscriptionModelChange}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="gpt-4o-mini-transcribe">
                    gpt-4o-mini-transcribe
                  </SelectItem>
                  <SelectItem value="whisper-large-v3">
                    whisper-large-v3
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Chat Model Selection */}
            <div className="space-y-2">
              <Label className="text-xs font-medium">Chat Model</Label>
              <Select value={chatModel} onValueChange={handleChatModelChange}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="gpt-4o-mini">gpt-4o-mini</SelectItem>
                  <SelectItem value="gpt-4o">gpt-4o</SelectItem>
                  <SelectItem value="gpt-4-turbo">gpt-4-turbo</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Role Simulation */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-medium">Role Simulation</Label>
                <Switch
                  checked={isRoleSimulationEnabled}
                  onCheckedChange={handleRoleSimulationToggle}
                />
              </div>
              {isRoleSimulationEnabled && (
                <div className="space-y-1">
                  <Select
                    value={simulatedRoles.length > 0 ? simulatedRoles[0] : ''}
                    onValueChange={handleRoleChange}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Select a role to simulate" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sysAdmin">sysAdmin</SelectItem>
                      <SelectItem value="orgAdmin">orgAdmin</SelectItem>
                      <SelectItem value="storeManager">storeManager</SelectItem>
                      <SelectItem value="staff">staff</SelectItem>
                      <SelectItem value="cashier">cashier</SelectItem>
                      <SelectItem value="inventoryClerk">
                        inventoryClerk
                      </SelectItem>
                      <SelectItem value="auditor">auditor</SelectItem>
                      <SelectItem value="sysSupport">sysSupport</SelectItem>
                      <SelectItem value="customer">customer</SelectItem>
                    </SelectContent>
                  </Select>
                  {simulatedRoles.length > 0 && (
                    <p className="text-[10px] text-muted-foreground">
                      Simulating: {simulatedRoles.join(', ')}
                    </p>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
};
